/**
 * Keeps mandate Individual Space navigation inside /apps/mandate/* when platform
 * pages emit hardcoded /prioritization/… links or navigate() calls.
 *
 * The shell flag lives in sessionStorage so a reload of an embedded page keeps
 * its shell. It is cleared when the Mandate layout unmounts, when "Open full
 * Priorities" or the area switcher is used — and here, when the document itself
 * opens anywhere outside Mandate: a person who typed or followed a Priorities
 * address after a visit to Mandate has left it, and must not be carried back.
 */

import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  MANDATE_BASE,
  isMandateShellActive,
  rewritePathForMandateShell,
  setMandateShellActive,
} from '../../lib/productSurfacePaths';

export function MandatePathGuard() {
  const location = useLocation();
  const navigate = useNavigate();
  const openedOutsideMandate = useRef<boolean | null>(null);

  if (openedOutsideMandate.current === null) {
    openedOutsideMandate.current = !location.pathname.startsWith(MANDATE_BASE);
  }

  useEffect(() => {
    // Runs before the rewrite effect below (declaration order), once per document.
    if (openedOutsideMandate.current) setMandateShellActive(false);
  }, []);

  useEffect(() => {
    if (!isMandateShellActive()) return;
    if (!location.pathname.startsWith('/prioritization/')) return;

    const rewritten = rewritePathForMandateShell(
      `${location.pathname}${location.search}${location.hash}`
    );
    if (rewritten !== `${location.pathname}${location.search}${location.hash}`) {
      navigate(rewritten, { replace: true });
    }
  }, [location.pathname, location.search, location.hash, navigate]);

  return null;
}
